import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useWorkspace } from "@/lib/WorkspaceContext";
import { ArrowRight, Copy, MoveRight, Loader2, CheckCircle, AlertTriangle, X } from "lucide-react";
import { toast } from "sonner";
import { authFetch } from "@/lib/api";

const API = "";

export default function TransferHoldingsModal({ open, onClose }) {
  const { scope, accounts, refreshAccounts } = useWorkspace();
  const [mode, setMode] = useState("clone");
  const [sourceId, setSourceId] = useState("");
  const [destId, setDestId] = useState("");
  const [preserveCost, setPreserveCost] = useState(true);
  const [includeNotes, setIncludeNotes] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);
  const [step, setStep] = useState(1);

  useEffect(() => {
    if (open && scope) {
      setSourceId(scope.account_id || "");
      setDestId("");
      setStep(1);
      setPreview(null);
      setResult(null);
    }
  }, [open, scope]);

  const fetchPreview = async () => {
    if (!sourceId || !destId) return;
    setLoading(true);
    try {
      const res = await authFetch(
        `${API}/api/transfers/preview?source_account_id=${sourceId}&dest_account_id=${destId}`
      );
      if (res.ok) {
        setPreview(await res.json());
        setStep(2);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to load preview");
      }
    } catch {
      toast.error("Failed to load preview");
    } finally {
      setLoading(false);
    }
  };

  const executeTransfer = async () => {
    setExecuting(true);
    try {
      const res = await authFetch(`${API}/api/transfers`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          source_account_id: sourceId,
          dest_account_id: destId,
          mode,
          preserve_avg_cost: preserveCost,
          include_notes: includeNotes,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setResult(data);
        setStep(3);
        toast.success(`${mode === "clone" ? "Cloned" : "Moved"} ${data.holdings_count} holdings`);
        if (refreshAccounts && scope?.workspace_id) {
          refreshAccounts(scope.workspace_id);
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || "Transfer failed");
      }
    } catch {
      toast.error("Transfer failed");
    } finally {
      setExecuting(false);
    }
  };

  if (!open) return null;

  const otherAccounts = accounts.filter((a) => a.account_id !== sourceId);
  const sourceAcct = accounts.find((a) => a.account_id === sourceId);
  const destAcct = accounts.find((a) => a.account_id === destId);

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="transfer-modal">
      <Card className="bg-zinc-900 border-zinc-700 w-full max-w-lg max-h-[80vh] overflow-y-auto">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base text-zinc-200">
            {mode === "clone" ? "Clone" : "Move"} Holdings
          </CardTitle>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-300">
            <X className="w-4 h-4" />
          </button>
        </CardHeader>
        <CardContent className="space-y-4">

          {/* Step 1: Configure */}
          {step === 1 && (
            <>
              {/* Mode toggle */}
              <div className="flex gap-2" data-testid="transfer-mode-selector">
                <button
                  onClick={() => setMode("clone")}
                  className={`flex-1 flex items-center gap-2 p-3 rounded-lg border text-sm transition-all ${
                    mode === "clone"
                      ? "border-blue-500/50 bg-blue-500/10 text-blue-400"
                      : "border-zinc-700 text-zinc-400 hover:border-zinc-600"
                  }`}
                  data-testid="mode-clone"
                >
                  <Copy className="w-4 h-4" />
                  <div className="text-left">
                    <div className="font-medium">Clone</div>
                    <div className="text-[10px] text-zinc-500">Copy holdings, source unchanged</div>
                  </div>
                </button>
                <button
                  onClick={() => setMode("move")}
                  className={`flex-1 flex items-center gap-2 p-3 rounded-lg border text-sm transition-all ${
                    mode === "move"
                      ? "border-orange-500/50 bg-orange-500/10 text-orange-400"
                      : "border-zinc-700 text-zinc-400 hover:border-zinc-600"
                  }`}
                  data-testid="mode-move"
                >
                  <MoveRight className="w-4 h-4" />
                  <div className="text-left">
                    <div className="font-medium">Move</div>
                    <div className="text-[10px] text-zinc-500">Transfer out from source</div>
                  </div>
                </button>
              </div>

              {/* Source */}
              <div>
                <label className="text-[11px] text-zinc-500 uppercase tracking-wider">Source Account</label>
                <select
                  value={sourceId}
                  onChange={(e) => { setSourceId(e.target.value); setDestId(""); }}
                  className="w-full mt-1 bg-zinc-800 border border-zinc-700 text-zinc-200 rounded-md px-3 py-2 text-sm"
                  data-testid="source-select"
                >
                  <option value="">Select source...</option>
                  {accounts.map((a) => (
                    <option key={a.account_id} value={a.account_id}>{a.name}</option>
                  ))}
                </select>
              </div>

              {/* Destination */}
              <div>
                <label className="text-[11px] text-zinc-500 uppercase tracking-wider">Destination Account</label>
                <select
                  value={destId}
                  onChange={(e) => setDestId(e.target.value)}
                  className="w-full mt-1 bg-zinc-800 border border-zinc-700 text-zinc-200 rounded-md px-3 py-2 text-sm"
                  data-testid="dest-select"
                >
                  <option value="">Select destination...</option>
                  {otherAccounts.map((a) => (
                    <option key={a.account_id} value={a.account_id}>{a.name}</option>
                  ))}
                </select>
              </div>

              {/* Options */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-xs text-zinc-400">
                  <input type="checkbox" checked={preserveCost} onChange={(e) => setPreserveCost(e.target.checked)} className="rounded" />
                  Preserve average cost basis
                </label>
                <label className="flex items-center gap-2 text-xs text-zinc-400">
                  <input type="checkbox" checked={includeNotes} onChange={(e) => setIncludeNotes(e.target.checked)} className="rounded" />
                  Include notes / tags
                </label>
              </div>

              <Button
                onClick={fetchPreview}
                disabled={!sourceId || !destId || loading}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
                data-testid="preview-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                Preview Transfer
              </Button>
            </>
          )}

          {/* Step 2: Confirm */}
          {step === 2 && preview && (
            <>
              <div className="flex items-center gap-2 text-xs text-zinc-400 mb-2">
                <Badge variant="outline" className="border-zinc-600">{sourceAcct?.name}</Badge>
                <ArrowRight className="w-3 h-3" />
                <Badge variant="outline" className={mode === "clone" ? "border-blue-500/30 text-blue-400" : "border-orange-500/30 text-orange-400"}>
                  {destAcct?.name}
                </Badge>
                <Badge variant="outline" className="text-[10px] border-zinc-600 text-zinc-500 ml-auto">
                  {mode}
                </Badge>
              </div>

              {/* Summary */}
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="p-2 bg-zinc-800 rounded">
                  <div className="text-lg font-bold text-zinc-100">{preview.summary.holdings_count}</div>
                  <div className="text-[10px] text-zinc-500">Holdings</div>
                </div>
                <div className="p-2 bg-zinc-800 rounded">
                  <div className="text-lg font-bold text-zinc-100">${(preview.summary.total_market_value_usd || 0).toLocaleString()}</div>
                  <div className="text-[10px] text-zinc-500">Market Value</div>
                </div>
                <div className="p-2 bg-zinc-800 rounded">
                  <div className="text-lg font-bold text-zinc-100">{preview.summary.conflicts || 0}</div>
                  <div className="text-[10px] text-zinc-500">Conflicts</div>
                </div>
              </div>

              {preview.summary.conflicts > 0 && (
                <div className="flex items-start gap-2 p-2 bg-amber-500/5 border border-amber-500/20 rounded text-[11px] text-amber-300">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  {preview.summary.conflicts_note}
                </div>
              )}

              {/* Holdings list */}
              <div className="max-h-48 overflow-y-auto space-y-1">
                {preview.holdings.map((h) => (
                  <div key={h.ticker} className="flex items-center justify-between text-xs py-1 border-b border-zinc-800/50">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-zinc-200 w-12">{h.ticker}</span>
                      <span className="text-zinc-500">{h.shares} shares</span>
                      {h.exists_in_dest && (
                        <Badge variant="outline" className="text-[9px] border-amber-500/30 text-amber-400">merge</Badge>
                      )}
                    </div>
                    <span className="text-zinc-400">${h.market_value_usd?.toLocaleString()}</span>
                  </div>
                ))}
              </div>

              {mode === "move" && (
                <div className="flex items-start gap-2 p-2 bg-orange-500/5 border border-orange-500/20 rounded text-[11px] text-orange-300">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  Move mode: source holdings will be zeroed out and marked as transferred. This cannot be undone.
                </div>
              )}

              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setStep(1)} className="flex-1 border-zinc-700 text-zinc-400">
                  Back
                </Button>
                <Button
                  onClick={executeTransfer}
                  disabled={executing}
                  className={`flex-1 text-white gap-2 ${
                    mode === "clone" ? "bg-blue-600 hover:bg-blue-700" : "bg-orange-600 hover:bg-orange-700"
                  }`}
                  data-testid="execute-btn"
                >
                  {executing ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {mode === "clone" ? "Clone Holdings" : "Move Holdings"}
                </Button>
              </div>
            </>
          )}

          {/* Step 3: Success */}
          {step === 3 && result && (
            <div className="text-center space-y-3 py-4">
              <CheckCircle className="w-10 h-10 text-emerald-500 mx-auto" />
              <div className="text-zinc-200 font-medium">
                {result.mode === "clone" ? "Cloned" : "Moved"} {result.holdings_count} holdings
              </div>
              <div className="text-xs text-zinc-500">
                {result.source_account_name} → {result.dest_account_name}
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="p-2 bg-zinc-800 rounded">
                  <div className="text-zinc-300">{result.created_holdings} new</div>
                  <div className="text-zinc-500">holdings created</div>
                </div>
                <div className="p-2 bg-zinc-800 rounded">
                  <div className="text-zinc-300">${(result.total_value_usd || 0).toLocaleString()}</div>
                  <div className="text-zinc-500">total value</div>
                </div>
              </div>
              <Button onClick={onClose} className="w-full bg-zinc-700 hover:bg-zinc-600 text-zinc-200">
                Done
              </Button>
            </div>
          )}

        </CardContent>
      </Card>
    </div>
  );
}
